import React, { useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Fade, LinearProgress } from "@material-ui/core";

import { useOAuthToken } from "./hooks/useOAuthToken";
import { useAuthSessionContext } from "./AuthSessionProvider";
import { useGlobalError } from "../ErrorModal";
import { ErrorLevel } from "../ErrorModal/ErrorProvider";

/**
 * OAuth2Callback (RouteComponent)
 * Purpose:
 *  1. redirect target from cognito, receives "code" and "state" via qs
 *  2. validate the return data from cognito is trusted by comparing it
 *     to the local session data (uuid)
 *  3. on error: show error with button that goes to /logout
 *  4. on success: clear session info, and redirect to referrer
 */
export const OAuth2Callback = () => {
  const history = useHistory();

  const { authSession, finishSession } = useAuthSessionContext();
  const { loading, error } = useOAuthToken(authSession);
  const { setError } = useGlobalError();

  const sessionInitialPath = authSession?.sessionInitialPath;
  const isUnmounting = useRef(false);

  const redirectToLoginPage = useCallback(() => {
    finishSession();
    history.replace("/login");
  }, [finishSession, history]);

  useEffect(() => {
    if (isUnmounting.current) {
      return;
    }
    // no session
    if (authSession === null) {
      // if on the page without a session, we can't proceed further
      // redireact to the login page to restart login process
      isUnmounting.current = true;
      redirectToLoginPage();
      return;
    }
    if (!loading && !error) {
      finishSession();
      history.replace(sessionInitialPath || "/");
    } else if (error) {
      isUnmounting.current = true;
      setError({
        title: "Failed to log in",
        message: error.message,
        handlerText: "Try again",
        onClearError: redirectToLoginPage,
        level: ErrorLevel.Error,
      });
    }
  }, [
    loading,
    error,
    finishSession,
    history,
    authSession,
    sessionInitialPath,
    setError,
    redirectToLoginPage,
  ]);

  // Logic Component, so show loader while things execute
  return (
    <Fade in={true} unmountOnExit>
      <LinearProgress />
    </Fade>
  );
};
